import React, {
  FC,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  Grid2 as Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import { debounce } from '@mui/material/utils';
import FilterList from '@/components/Filter/FilterList/FilterList';
import log from 'loglevel';
import ExportJobTable from '@/components/ExportJobTable/ExportJobTable';
import { useMessageQueue } from '@erickson/components';
import { ListService } from '@/service/list/ListService';
import { useServices } from '@/components/ServiceContext';
import useFilterSelections from '@/pages/hooks/useFilterSelections';
import { FilterDefinition } from '@lambda-layer/models/FilterDefinition/FilterDefinition.ts';
import UserFilterTable from '@/components/UserFilterTable/UserFilterTable';
import { UserFilter } from '@/API';
import { ListType } from '@/service/ExportJobService';
import {
  FilterRowSelection,
  FilterSelections,
  RelationalOperator,
} from '@lambda-layer/types/filter';
import { isUserInGroup } from '@/auth/EricksonAuthenticator.tsx';
import { UserContext } from '@/context/userContext.tsx';

// Removed unused CustomTabPanel - tabs are managed directly in the component

const MAX_XLSX_ROWS = 1_048_575;

interface ListFilterPageProps {
  exportJobName: (filterSelections: FilterSelections) => string;
  listType: ListType;
  listService: ListService<unknown>;
  filterDefinitions: FilterDefinition[];
}

const ListFilterPage: FC<ListFilterPageProps> = ({
  exportJobName,
  listType,
  listService,
  filterDefinitions,
}) => {
  const userContext = useContext(UserContext);
  const { exportJobService, userFilterService, exclusionCacheService } =
    useServices();

  const exclusions = exclusionCacheService.getExclusions(listType);

  // Initialize useFilterSelections with cached exclusions
  const {
    filterSelections,
    fieldSelectionCountMap,
    loading,
    updateSelection,
    handleFocusChange,
    handleChange,
    calculateFilterCounts,
    resetAllFilters,
  } = useFilterSelections(listService, filterDefinitions, listType, exclusions);

  // UI states
  const [tabIndex, setTabIndex] = useState(0);
  const [useDefaultExportName, setUseDefaultExportName] = useState(true);
  const [userProvidedExportName, setUserProvidedExportName] = useState('');
  const [selectedFormat, setSelectedFormat] = useState<'xlsx' | 'csv'>('csv');
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const [selectedRecordCount, setSelectedRecordCount] = useState<number>(0);

  const [hideAdvancedFilters, setHideAdvancedFilters] = useState(true);

  // Compute filter definitions by overriding the isAdvancedFilter property for definitions that have it.
  const computedFilters = useMemo(() => {
    return filterDefinitions.map(filterDef =>
      typeof filterDef.isAdvancedFilter !== 'undefined'
        ? FilterDefinition.deserialize({
            ...filterDef,
            isAdvancedFilter: hideAdvancedFilters,
          })
        : filterDef
    );
  }, [filterDefinitions, hideAdvancedFilters]);

  // Identify the limit field if it exists
  const limitFieldDefinition = useMemo(
    () => filterDefinitions.find(fd => fd.csvMetadata?.fieldType === 'limit'),
    [filterDefinitions]
  );

  const messageQueue = useRef(useMessageQueue()).current;

  // Parse the limit field value from the selections
  const limitFieldValue = useMemo(() => {
    if (!limitFieldDefinition) return undefined;
    const selection = filterSelections[limitFieldDefinition.id]?.value[0];
    if (typeof selection === 'number') {
      return selection;
    }
    if (typeof selection === 'string') {
      const parsed = parseInt(selection, 10);
      return !isNaN(parsed) ? parsed : undefined;
    }
    return undefined;
  }, [filterSelections, limitFieldDefinition]);

  const isLoadingOverall = useMemo(
    () => Object.values(loading).some(Boolean),
    [loading]
  );

  /**
   * Determines if a warning should be shown when exporting XLSX with too many records.
   */
  const showXlsxLimitWarning = useMemo(() => {
    const exceedsXlsxLimit = selectedRecordCount > MAX_XLSX_ROWS;
    const limitIsInvalid =
      limitFieldValue === undefined || limitFieldValue > MAX_XLSX_ROWS;
    return selectedFormat === 'xlsx' && limitIsInvalid && exceedsXlsxLimit;
  }, [selectedFormat, limitFieldValue, selectedRecordCount]);

  /**
   * Export records to the specified format.
   */
  const handleExportRecords = useCallback(
    async (
      exportName: string,
      filterSelections: FilterSelections,
      format: 'xlsx' | 'csv'
    ) => {
      try {
        await exportJobService.createExportJob(
          listType,
          exportName,
          filterDefinitions,
          filterSelections,
          undefined,
          format
        );
        messageQueue.addMessage({
          text: 'Export job created successfully',
          severity: 'success',
        });
      } catch (err) {
        log.error('Error creating export job:', err);
        messageQueue.addMessage({
          text: 'Error creating the export job',
          severity: 'error',
        });
      }
    },
    [exportJobService, listType, filterDefinitions, messageQueue]
  );

  const [triggerRedraw, setTriggerRedraw] = useState(0);
  /**
   * Handles applying a given set of filter selections in sequence.
   */
  const handleApplyFilterSelections = useCallback(
    async (incomingSelections: FilterSelections) => {
      // Reset redraw trigger to force re-render
      setTriggerRedraw(prev => prev + 1);

      setIsApplyingFilters(true);
      try {
        // Reset to clear existing state
        resetAllFilters();

        let cumulativeSelection: FilterSelections = {};

        // Apply each filter in order
        for (const fd of filterDefinitions) {
          const filterSelection: FilterRowSelection | undefined =
            incomingSelections[fd.id];
          if (filterSelection) {
            await handleFocusChange(fd.id, cumulativeSelection);
            await handleChange(fd.id, filterSelection, cumulativeSelection);
            await updateSelection(fd.id, filterSelection);
            cumulativeSelection = {
              ...cumulativeSelection,
              [fd.id]: filterSelection,
            };
          }
        }
      } finally {
        setIsApplyingFilters(false);
      }
    },
    [
      resetAllFilters,
      filterDefinitions,
      handleFocusChange,
      handleChange,
      updateSelection,
    ]
  );

  /**
   * Saves a user filter.
   */
  const handleUserFilterSave = useCallback(
    async (
      filterName: string,
      filterDefinitions: FilterDefinition[],
      filterSelections: Record<string, FilterRowSelection>
    ) => {
      try {
        const createdUserFilter = await userFilterService.createUserFilter(
          filterName,
          listType,
          filterDefinitions,
          filterSelections
        );
        // Automatically switch to the "Saved Filters" tab after saving
        setTabIndex(1);
        return createdUserFilter;
      } catch (error) {
        log.error('Error creating user filter:', error);
        throw error;
      }
    },
    [userFilterService, listType]
  );

  /**
   * Deletes a user filter.
   */
  const handleUserFilterDelete = useCallback(
    (userFilter: UserFilter) => {
      void userFilterService.delete(userFilter.id);
    },
    [userFilterService]
  );

  /**
   * Debounced handleChange to reduce excessive updates.
   */
  const handleChangeCallback = useCallback(
    (fieldId: string, filterRowSelection: FilterRowSelection) => {
      void handleChange(fieldId, filterRowSelection);
    },
    [handleChange]
  );

  const debouncedHandleChange = useMemo(
    () => debounce(handleChangeCallback, 300),
    [handleChangeCallback]
  );

  /**
   * Attempt to export when user clicks export button.
   */
  const handleExportClick = useCallback(() => {
    const exportName = useDefaultExportName
      ? exportJobName(filterSelections)
      : userProvidedExportName;
    void handleExportRecords(exportName, filterSelections, selectedFormat);
  }, [
    exportJobName,
    filterSelections,
    useDefaultExportName,
    userProvidedExportName,
    handleExportRecords,
    selectedFormat,
  ]);

  /**
   * Sets the limit field to the maximum XLSX rows if applicable.
   */
  const handleSetLimitToMax = useCallback(async () => {
    if (!limitFieldDefinition) return;
    const limitFieldId = limitFieldDefinition.id;
    const newSelection: FilterRowSelection = {
      relationalOperator: RelationalOperator.EQUAL,
      value: [MAX_XLSX_ROWS],
    };
    await updateSelection(limitFieldId, newSelection);
  }, [limitFieldDefinition, updateSelection]);

  // Handlers for controlled form inputs
  const handleUseDefaultExportNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setUseDefaultExportName(event.target.checked);
    },
    []
  );

  const handleUserProvidedExportNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setUserProvidedExportName(event.target.value);
    },
    []
  );

  const handleFormatChange = useCallback(
    (event: SelectChangeEvent<'xlsx' | 'csv'>) => {
      setSelectedFormat(event.target.value as 'xlsx' | 'csv');
    },
    []
  );

  return (
    <>
      <Container>
        {/* Toggle for Advanced Filters */}
        {userContext && isUserInGroup(userContext.user, 'Admin') && (
          <Box mb={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={!hideAdvancedFilters}
                  onChange={e => setHideAdvancedFilters(!e.target.checked)}
                  color="primary"
                />
              }
              label="Show advanced filters"
            />
          </Box>
        )}

        <Grid container spacing={3} mb={2}>
          <Grid size={{ xs: 6 }}>
            <FilterList
              key={triggerRedraw} // Force re-render on filter changes
              filterDefinitions={computedFilters}
              filterSelections={filterSelections}
              filterCounts={fieldSelectionCountMap}
              loading={loading}
              updateSelection={updateSelection}
              onFocusChange={handleFocusChange}
              onChange={debouncedHandleChange}
              calculateFilterCounts={calculateFilterCounts} // Pass as a function reference
              onFilterSave={handleUserFilterSave}
              onFilterSelectionCountChange={setSelectedRecordCount}
            />
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Tabs
              value={tabIndex}
              onChange={(_, newVal) => setTabIndex(newVal)}
            >
              <Tab label="Export Jobs" />
              <Tab label="Saved Filters" />
            </Tabs>
            {/* This workaround is needed DataGrid due to a "The parent DOM element of the Data Grid has an empty height" */}
            {/* It renders the DataGrid off-screen and then moves it into view rather than using display:none */}
            <Box
              sx={{
                visibility: tabIndex === 0 ? 'visible' : 'hidden',
                position: tabIndex === 0 ? 'relative' : 'absolute',
                top: tabIndex === 0 ? 'auto' : '-9999px',
                width: '100%',
                p: 3,
              }}
            >
              <ExportJobTable
                listType={listType}
                filterDefinitions={filterDefinitions}
                onApplyUserFilter={handleApplyFilterSelections}
              />
            </Box>
            <Box
              sx={{
                visibility: tabIndex === 1 ? 'visible' : 'hidden',
                position: tabIndex === 1 ? 'relative' : 'absolute',
                top: tabIndex === 1 ? 'auto' : '-9999px',
                width: '100%',
                p: 3,
              }}
            >
              <UserFilterTable
                listType={listType}
                filterDefinitions={filterDefinitions}
                onApplyFilterSelections={handleApplyFilterSelections}
                onDelete={handleUserFilterDelete}
              />
            </Box>
          </Grid>
        </Grid>

        {showXlsxLimitWarning && (
          <Grid container>
            <Grid>
              <Box mt={2}>
                <Alert severity="warning">
                  The total number of records (
                  {selectedRecordCount.toLocaleString()}) exceeds the maximum
                  rows ({MAX_XLSX_ROWS.toLocaleString()}) allowed in XLSX.
                  {limitFieldDefinition ? (
                    <>
                      {' '}
                      <Link
                        component="button"
                        variant="body2"
                        onClick={handleSetLimitToMax}
                      >
                        Click here
                      </Link>{' '}
                      to set the limit to {MAX_XLSX_ROWS.toLocaleString()} and
                      continue with XLSX. Or switch to CSV format below.
                    </>
                  ) : (
                    ' Please switch to CSV format.'
                  )}
                </Alert>
              </Box>
            </Grid>
          </Grid>
        )}

        <Grid container spacing={2} mt={1} alignItems="center">
          <Grid>
            <Button
              sx={{ height: 40 }}
              variant="contained"
              disabled={
                showXlsxLimitWarning ||
                (!useDefaultExportName && userProvidedExportName.length === 0)
              }
              onClick={handleExportClick}
              aria-label="Export Records"
            >
              Export Records
            </Button>
          </Grid>

          <Grid>
            <Grid container spacing={2} alignItems="center">
              <Grid>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="export-format-label">Format</InputLabel>
                  <Select
                    labelId="export-format-label"
                    id="export-format-select"
                    value={selectedFormat}
                    label="Format"
                    onChange={handleFormatChange}
                  >
                    <MenuItem value="xlsx">XLSX</MenuItem>
                    <MenuItem value="csv">CSV</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid>
                <TextField
                  label="Export Name"
                  disabled={useDefaultExportName}
                  size="small"
                  value={
                    useDefaultExportName
                      ? exportJobName(filterSelections)
                      : userProvidedExportName
                  }
                  onChange={handleUserProvidedExportNameChange}
                  sx={{ width: 300 }}
                />
              </Grid>
              <Grid>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={useDefaultExportName}
                      onChange={handleUseDefaultExportNameChange}
                    />
                  }
                  label="Use default export name"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <br />
      </Container>

      <Backdrop
        sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }}
        open={isLoadingOverall && isApplyingFilters}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default ListFilterPage;
